import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  trackWhatsAppClick,
  trackEventCustom,
} from "../../utils/analytics";

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/services/inventory-management`;

const features = [
  {
    title: "Centralized Inventory Management",
    text: "Maintain centralized inventory records across products, materials, warehouses, stores and business locations with clear stock visibility.",
  },
  {
    title: "Stock Inward & Outward",
    text: "Track material receipts, stock issues, transfers and outward transactions while maintaining complete inventory movement history.",
  },
  {
    title: "Purchase Management",
    text: "Manage suppliers, purchase orders, received materials, purchase transactions and inventory updates through connected workflows.",
  },
  {
    title: "Sales & Stock Deduction",
    text: "Connect sales transactions with inventory so stock quantities can be updated automatically and operational teams can monitor availability.",
  },
  {
    title: "Stock Transfer Management",
    text: "Move inventory between warehouses, stores or business locations while maintaining accurate transfer records and stock balances.",
  },
  {
    title: "Supplier & Vendor Management",
    text: "Maintain supplier information, purchasing history and vendor-related inventory transactions from one centralized platform.",
  },
  {
    title: "Stock History & Traceability",
    text: "View detailed transaction history to understand when stock was received, issued, transferred, adjusted or consumed.",
  },
  {
    title: "Inventory Reports & Dashboards",
    text: "Generate operational reports and dashboards for stock availability, purchases, consumption, movements and inventory performance.",
  },
];

const workflows = [
  {
    number: "01",
    title: "Product & Item Master",
    text: "Create and manage products, materials, categories, units, specifications and other inventory-related master information.",
  },
  {
    number: "02",
    title: "Purchase & Stock Inward",
    text: "Record purchase transactions and received materials while updating available inventory quantities.",
  },
  {
    number: "03",
    title: "Stock Movement",
    text: "Track inward, outward, transfer, consumption and adjustment transactions with complete movement history.",
  },
  {
    number: "04",
    title: "Sales & Material Issue",
    text: "Connect sales, material requests or operational issues with inventory so stock movement remains synchronized.",
  },
  {
    number: "05",
    title: "Reports & Monitoring",
    text: "Use dashboards and reports to monitor stock levels, inventory movements, purchases and operational performance.",
  },
];

const benefits = [
  "Real-time stock visibility",
  "Reduced manual inventory work",
  "Centralized inventory records",
  "Better purchase planning",
  "Accurate stock movement tracking",
  "Improved vendor management",
  "Reduced inventory discrepancies",
  "Faster operational reporting",
];

const useCases = [
  {
    title: "Manufacturing Businesses",
    text: "Manage raw materials, components, production-related inventory, stores and material consumption.",
  },
  {
    title: "Trading & Distribution",
    text: "Track products purchased from suppliers and sold to customers with accurate stock balances.",
  },
  {
    title: "Retail Businesses",
    text: "Maintain product inventory, stock movements, purchasing and sales-related stock updates.",
  },
  {
    title: "Industrial Operations",
    text: "Manage materials, stores, inward/outward transactions, employee requests and operational stock usage.",
  },
  {
    title: "Multi-Location Businesses",
    text: "Monitor inventory across multiple stores, warehouses or operational locations.",
  },
  {
    title: "Project-Based Businesses",
    text: "Track materials issued and consumed for projects, jobs or specific business activities.",
  },
];

const faqs = [
  {
    question: "What is custom inventory management software?",
    answer:
      "Custom inventory management software is a business application developed around an organization's specific inventory workflows, products, warehouses, purchasing processes, stock movements, approvals and reporting requirements.",
  },
  {
    question: "Can HexxaTech develop inventory software for manufacturing businesses?",
    answer:
      "Yes. HexxaTech develops inventory management systems for manufacturing and industrial businesses covering materials, stores, inward and outward transactions, stock transfers, material requests, consumption and operational reporting.",
  },
  {
    question: "Can inventory software integrate with ERP and CRM systems?",
    answer:
      "Yes. Inventory management software can be integrated with ERP, CRM, accounting, sales, purchasing, production and other business applications through APIs and database-driven integrations.",
  },
  {
    question: "Can the system support multiple warehouses?",
    answer:
      "Yes. A custom inventory system can be designed to manage multiple warehouses, stores, branches or business locations with location-wise stock visibility and transfer management.",
  },
  {
    question: "Can inventory reports and dashboards be customized?",
    answer:
      "Yes. Reports and dashboards can be developed around the specific metrics your business needs, including stock availability, purchases, inward and outward transactions, consumption, transfers and inventory history.",
  },
  {
    question: "Why choose custom inventory software instead of generic software?",
    answer:
      "Custom software can be designed around your actual business processes instead of requiring your team to change its workflow to match a generic application. This can provide better integration, automation and long-term scalability.",
  },
];

const serviceNavigation = [
  { title: "Custom Software Development", to: "/services/custom-software-development" },
  { title: "ERP & Business Management", to: "/services/erp-business-management" },
  { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
  { title: "Healthcare Software", to: "/services/healthcare-software" },
  { title: "Inventory Management", to: "/services/inventory-management" },
  { title: "Industrial Process Software", to: "/services/industrial-process-software" },
  { title: "License Management", to: "/services/license-management" },
  { title: "Mobile App Development", to: "/services/mobile-app-development" },
];

export default function InventoryManagement() {
  useEffect(() => {
    document.title =
      "Inventory Management Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech Softwares develops custom inventory management software for businesses in Pune and across India, including stock management, purchase management, inward and outward tracking, warehouses, vendors, inventory reports and business automation.";

    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setMeta("description", description);

    setMeta(
      "keywords",
      "inventory management software development, inventory management software Pune, custom inventory software, stock management software, warehouse management software, inventory software development company, inventory management system India"
    );

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", PAGE_URL);

    const setProperty = (property, content) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setProperty(
      "og:title",
      "Inventory Management Software Development | HexxaTech"
    );

    setProperty(
      "og:description",
      "Custom inventory management software for stock, purchasing, warehouses, vendors, stock movements, reporting and business operations."
    );

    setProperty("og:type", "website");
    setProperty("og:url", PAGE_URL);
    setProperty("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "inventory-management-schema"
    );

    if (!existingSchema) {
      const script = document.createElement("script");

      script.id = "inventory-management-schema";
      script.type = "application/ld+json";

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Inventory Management Software Development",
        serviceType: "Inventory Management Software Development",
        provider: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: SITE_URL,
        },
        areaServed: {
          "@type": "Country",
          name: "India",
        },
        description: description,
        url: PAGE_URL,
      });

      document.head.appendChild(script);
    }

    const faqSchemaId = "inventory-management-faq-schema";

    const existingFaqSchema = document.getElementById(faqSchemaId);

    if (!existingFaqSchema) {
      const faqScript = document.createElement("script");

      faqScript.id = faqSchemaId;
      faqScript.type = "application/ld+json";

      faqScript.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      });

      document.head.appendChild(faqScript);
    }
  }, []);

  const handleWhatsApp = (location) => {
    trackWhatsAppClick(location);
  };

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/inventory-management"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="inventory-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .inventory-page {
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .inventory-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .inventory-hero {
          position: relative;
          overflow: hidden;
          padding: 105px 0 90px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 20%,
              rgba(59, 130, 246, 0.22),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .inventory-hero::before {
          content: "";
          position: absolute;
          width: 460px;
          height: 460px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.08);
          right: -180px;
          top: -220px;
        }

        .inventory-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 65px;
          align-items: center;
        }

        .inventory-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          margin-bottom: 20px;
          border-radius: 999px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.15);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.1px;
          text-transform: uppercase;
        }

        .inventory-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .inventory-hero h1 {
          max-width: 750px;
          margin: 0;
          font-size: clamp(40px, 5vw, 65px);
          line-height: 1.04;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .inventory-hero h1 span {
          color: #60a5fa;
        }

        .inventory-hero-text {
          max-width: 670px;
          margin: 24px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .inventory-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .inventory-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 800;
          transition: .25s ease;
        }

        .inventory-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15,23,42,.2);
        }

        .inventory-btn-primary:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
        }

        .inventory-btn-light {
          color: #ffffff;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.2);
        }

        .inventory-btn-light:hover {
          background: rgba(255,255,255,.15);
        }

        /* =========================
           HERO VISUAL
        ========================= */

        .inventory-dashboard {
          position: relative;
          padding: 16px;
          border-radius: 22px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.18);
          box-shadow: 0 30px 70px rgba(15,23,42,.25);
          backdrop-filter: blur(10px);
          transform: rotate(-2deg);
        }

        .inventory-dashboard-top {
          display: flex;
          gap: 6px;
          padding-bottom: 14px;
        }

        .inventory-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,.6);
        }

        .inventory-dashboard-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          margin-bottom: 12px;
          padding: 14px;
          border-radius: 12px;
          background: rgba(255,255,255,.09);
        }

        .inventory-dashboard-header strong {
          font-size: 13px;
        }

        .inventory-dashboard-header span {
          font-size: 9px;
          color: #bfdbfe;
        }

        .inventory-dashboard-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 12px;
        }

        .inventory-dashboard-card {
          min-height: 80px;
          padding: 13px;
          border-radius: 12px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.08);
        }

        .inventory-dashboard-card strong {
          display: block;
          font-size: 18px;
          margin-bottom: 5px;
        }

        .inventory-dashboard-card small {
          color: #cbd5e1;
          font-size: 8px;
        }

        .inventory-table {
          display: grid;
          gap: 7px;
          padding: 14px;
          border-radius: 14px;
          background: rgba(255,255,255,.08);
        }

        .inventory-table-row {
          display: grid;
          grid-template-columns: 1.5fr .7fr .7fr;
          gap: 8px;
          padding: 9px;
          border-radius: 7px;
          background: rgba(255,255,255,.07);
          font-size: 9px;
          color: #dbeafe;
        }

        .inventory-table-row:first-child {
          color: #ffffff;
          font-weight: 800;
        }

        /* =========================
           INTRO
        ========================= */

        .inventory-section {
          padding: 90px 0;
        }

        .inventory-section-soft {
          background: #f8fafc;
        }

        .inventory-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .inventory-title {
          max-width: 780px;
          margin: 12px 0 0;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .inventory-description {
          max-width: 760px;
          margin: 18px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.85;
        }

        .inventory-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .inventory-intro-text {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
        }

        .inventory-highlight-box {
          padding: 32px;
          border-radius: 22px;
          background: linear-gradient(
            145deg,
            #eff6ff,
            #ffffff
          );
          border: 1px solid #dbeafe;
        }

        .inventory-highlight-box h3 {
          margin: 0 0 20px;
          font-size: 21px;
        }

        .inventory-highlight-list {
          display: grid;
          gap: 11px;
        }

        .inventory-highlight-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 12px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
        }

        .inventory-highlight-item span {
          color: #2563eb;
          font-weight: 900;
        }

        /* =========================
           FEATURES
        ========================= */

        .inventory-header-center {
          max-width: 780px;
          margin: 0 auto 45px;
          text-align: center;
        }

        .inventory-header-center .inventory-title,
        .inventory-header-center .inventory-description {
          margin-left: auto;
          margin-right: auto;
        }

        .inventory-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .inventory-feature {
          min-height: 220px;
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .inventory-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .inventory-feature-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 19px;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-weight: 900;
          font-size: 16px;
        }

        .inventory-feature h3 {
          margin: 0 0 9px;
          font-size: 16px;
        }

        .inventory-feature p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           WORKFLOW
        ========================= */

        .inventory-workflow-grid {
          display: grid;
          grid-template-columns: .85fr 1.15fr;
          gap: 70px;
          align-items: start;
        }

        .inventory-workflow-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .inventory-workflow-item {
          padding: 20px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .25s ease;
        }

        .inventory-workflow-item:hover {
          border-color: #bfdbfe;
          transform: translateX(4px);
        }

        .inventory-workflow-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1px;
        }

        .inventory-workflow-item h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .inventory-workflow-item p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .inventory-flow-box {
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(
            145deg,
            #eff6ff,
            #ffffff
          );
          border: 1px solid #dbeafe;
        }

        .inventory-flow {
          display: grid;
          gap: 10px;
        }

        .inventory-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .inventory-flow-number {
          width: 31px;
          height: 31px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 10px;
          font-weight: 900;
        }

        .inventory-flow-row strong {
          font-size: 12px;
        }

        /* =========================
           BENEFITS
        ========================= */

        .inventory-benefits-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-top: 35px;
        }

        .inventory-benefit {
          padding: 18px;
          border-radius: 13px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
          font-weight: 700;
        }

        .inventory-benefit::before {
          content: "✓";
          margin-right: 8px;
          color: #2563eb;
          font-weight: 900;
        }

        /* =========================
           USE CASES
        ========================= */

        .inventory-use-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
          margin-top: 40px;
        }

        .inventory-use-card {
          min-height: 190px;
          padding: 25px;
          border-radius: 17px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .inventory-use-card h3 {
          margin: 0 0 10px;
          font-size: 17px;
        }

        .inventory-use-card p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =========================
           CTA
        ========================= */

        .inventory-cta {
          padding: 85px 0;
        }

        .inventory-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          border-radius: 24px;
          text-align: center;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.3),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #0f172a,
              #172554
            );
        }

        .inventory-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.1);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .inventory-cta h2 {
          max-width: 700px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .inventory-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .inventory-cta .inventory-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .inventory-cta .inventory-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           FAQ
        ========================= */

        .inventory-faq {
          max-width: 900px;
          margin: 45px auto 0;
        }

        .inventory-faq-item {
          margin-bottom: 12px;
          padding: 20px 22px;
          border-radius: 13px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .inventory-faq-item h3 {
          margin: 0 0 9px;
          font-size: 15px;
        }

        .inventory-faq-item p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1000px) {
          .inventory-hero-grid,
          .inventory-intro-grid,
          .inventory-workflow-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .inventory-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .inventory-use-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .inventory-benefits-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .inventory-dashboard {
            max-width: 620px;
          }
        }

        @media (max-width: 700px) {
          .inventory-container {
            width: min(100% - 28px, 1180px);
          }

          .inventory-hero {
            padding: 75px 0 65px;
          }

          .inventory-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .inventory-hero-text {
            font-size: 15px;
          }

          .inventory-dashboard {
            transform: none;
          }

          .inventory-dashboard-cards {
            grid-template-columns: 1fr;
          }

          .inventory-feature-grid,
          .inventory-use-grid,
          .inventory-benefits-grid {
            grid-template-columns: 1fr;
          }

          .inventory-section {
            padding: 65px 0;
          }

          .inventory-flow-box {
            padding: 20px;
          }

          .inventory-cta {
            padding: 65px 0;
          }

          .inventory-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="inventory-hero">
        <div className="inventory-container inventory-hero-grid">
          <div>
            <div className="inventory-eyebrow">
              <span className="inventory-eyebrow-dot"></span>
              Inventory Management Software
            </div>

            <h1>
              Know what you have,
              <br />
              <span>where it is and how it moves.</span>
            </h1>

            <p className="inventory-hero-text">
              HexxaTech develops custom inventory management software that
              helps businesses manage stock, purchasing, warehouses,
              suppliers, inward and outward transactions, transfers,
              consumption and inventory reporting.
            </p>

            <div className="inventory-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20inventory%20management%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="inventory-btn inventory-btn-primary"
                onClick={() =>
                  handleWhatsApp("inventory_hero")
                }
              >
                Discuss Your Inventory System →
              </a>

              <Link
                to="/case-studies/inventory-management"
                className="inventory-btn inventory-btn-light"
                onClick={() =>
                  trackEventCustom("inventory_case_study_click", {
                    location: "inventory_hero",
                  })
                }
              >
                View Inventory Case Study →
              </Link>
            </div>
          </div>

          <div>
            <div className="inventory-dashboard">
              <div className="inventory-dashboard-top">
                <span className="inventory-dot"></span>
                <span className="inventory-dot"></span>
                <span className="inventory-dot"></span>
              </div>

              <div className="inventory-dashboard-header">
                <strong>Inventory Dashboard</strong>
                <span>LIVE STOCK OVERVIEW</span>
              </div>

              <div className="inventory-dashboard-cards">
                <div className="inventory-dashboard-card">
                  <strong>1,248</strong>
                  <small>Items in Stock</small>
                </div>

                <div className="inventory-dashboard-card">
                  <strong>86</strong>
                  <small>Low Stock Items</small>
                </div>

                <div className="inventory-dashboard-card">
                  <strong>24</strong>
                  <small>Warehouses</small>
                </div>
              </div>

              <div className="inventory-table">
                <div className="inventory-table-row">
                  <span>Item</span>
                  <span>Stock</span>
                  <span>Status</span>
                </div>

                <div className="inventory-table-row">
                  <span>Raw Material</span>
                  <span>420</span>
                  <span>Available</span>
                </div>

                <div className="inventory-table-row">
                  <span>Components</span>
                  <span>185</span>
                  <span>Available</span>
                </div>

                <div className="inventory-table-row">
                  <span>Finished Goods</span>
                  <span>640</span>
                  <span>Available</span>
                </div>

                <div className="inventory-table-row">
                  <span>Consumables</span>
                  <span>86</span>
                  <span>Low</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRODUCTION
      ========================================= */}

      <section className="inventory-section">
        <div className="inventory-container inventory-intro-grid">
          <div>
            <span className="inventory-label">
              Custom Inventory Software
            </span>

            <h2 className="inventory-title">
              Inventory management designed around your business.
            </h2>

            <p className="inventory-intro-text">
              Inventory processes can become difficult to manage when stock
              records are spread across spreadsheets, manual registers and
              disconnected applications.
            </p>

            <p className="inventory-intro-text">
              HexxaTech develops custom inventory management systems that
              centralize inventory information and connect purchasing,
              suppliers, stock movements, warehouses, sales and operational
              workflows in one platform.
            </p>

            <p className="inventory-intro-text">
              Instead of forcing your business to adapt to generic inventory
              software, we build the application around the way your teams
              actually receive, store, transfer, issue, consume and manage
              materials.
            </p>
          </div>

          <div className="inventory-highlight-box">
            <h3>
              What your inventory system can manage
            </h3>

            <div className="inventory-highlight-list">
              {[
                "Products & Item Master",
                "Suppliers & Vendors",
                "Purchase Transactions",
                "Stock Inward",
                "Stock Outward",
                "Warehouse Management",
                "Stock Transfers",
                "Material Requests",
                "Stock Adjustments",
                "Inventory Reports",
              ].map((item) => (
                <div
                  className="inventory-highlight-item"
                  key={item}
                >
                  <span>✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="inventory-section inventory-section-soft">
        <div className="inventory-container">
          <div className="inventory-header-center">
            <span className="inventory-label">
              Inventory Management Features
            </span>

            <h2 className="inventory-title">
              Everything you need to control inventory operations.
            </h2>

            <p className="inventory-description">
              Build a centralized inventory platform that gives your team
              better control over stock, purchases, suppliers, movements and
              operational reporting.
            </p>
          </div>

          <div className="inventory-feature-grid">
            {features.map((feature, index) => (
              <article
                className="inventory-feature"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("inventory_feature_interest", {
                    feature: feature.title,
                  })
                }
              >
                <div className="inventory-feature-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          WORKFLOW
      ========================================= */}

      <section className="inventory-section">
        <div className="inventory-container inventory-workflow-grid">
          <div>
            <span className="inventory-label">
              Inventory Workflow
            </span>

            <h2 className="inventory-title">
              From purchase to stock movement and reporting.
            </h2>

            <p className="inventory-description">
              Your inventory system can connect every important stock
              transaction so your team has a consistent and traceable view of
              inventory.
            </p>

            <div className="inventory-workflow-list">
              {workflows.map((workflow) => (
                <div
                  className="inventory-workflow-item"
                  key={workflow.number}
                >
                  <div className="inventory-workflow-number">
                    {workflow.number}
                  </div>

                  <h3>{workflow.title}</h3>

                  <p>{workflow.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="inventory-flow-box">
            <span className="inventory-label">
              Example Workflow
            </span>

            <h3
              style={{
                margin: "12px 0 25px",
                fontSize: "23px",
              }}
            >
              One connected inventory process.
            </h3>

            <div className="inventory-flow">
              {[
                "Purchase Order",
                "Material Received",
                "Stock Inward",
                "Warehouse / Store",
                "Material Issue / Sales",
                "Stock Balance",
                "Reports & Dashboard",
              ].map((item, index) => (
                <div
                  className="inventory-flow-row"
                  key={item}
                >
                  <div className="inventory-flow-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                color: "#64748b",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              Every transaction can be connected to the appropriate
              business process, creating better stock visibility and
              traceability.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================
          BENEFITS
      ========================================= */}

      <section className="inventory-section inventory-section-soft">
        <div className="inventory-container">
          <span className="inventory-label">
            Business Benefits
          </span>

          <h2 className="inventory-title">
            Better inventory control leads to better business decisions.
          </h2>

          <p className="inventory-description">
            A centralized inventory management platform can reduce manual
            work, improve operational visibility and give management reliable
            information for purchasing and business planning.
          </p>

          <div className="inventory-benefits-grid">
            {benefits.map((benefit) => (
              <div
                className="inventory-benefit"
                key={benefit}
              >
                {benefit}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          USE CASES
      ========================================= */}

      <section className="inventory-section">
        <div className="inventory-container">
          <div className="inventory-header-center">
            <span className="inventory-label">
              Who We Build For
            </span>

            <h2 className="inventory-title">
              Inventory software for different business models.
            </h2>

            <p className="inventory-description">
              Inventory requirements vary between industries. Custom
              development allows the system to reflect the specific way your
              business manages materials and products.
            </p>
          </div>

          <div className="inventory-use-grid">
            {useCases.map((useCase) => (
              <article
                className="inventory-use-card"
                key={useCase.title}
              >
                <h3>{useCase.title}</h3>

                <p>{useCase.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          FAQ
      ========================================= */}

      <section className="inventory-section inventory-section-soft">
        <div className="inventory-container">
          <div className="inventory-header-center">
            <span className="inventory-label">
              Frequently Asked Questions
            </span>

            <h2 className="inventory-title">
              Inventory management software questions.
            </h2>
          </div>

          <div className="inventory-faq">
            {faqs.map((faq) => (
              <article
                className="inventory-faq-item"
                key={faq.question}
              >
                <h3>{faq.question}</h3>

                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="inventory-label">
            Explore Our Services
          </span>

          <h2 className="inventory-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/inventory-management";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="inventory-cta">
        <div className="inventory-container">
          <div className="inventory-cta-box">
            <span className="inventory-cta-label">
              Build Your Inventory System
            </span>

            <h2>
              Need better control over your inventory?
            </h2>

            <p>
              Tell us how your business handles purchasing, stock,
              warehouses, material requests and inventory transactions. We
              can design a custom inventory management solution around your
              workflow.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20inventory%20management%20software%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="inventory-btn inventory-btn-primary"
              onClick={() =>
                handleWhatsApp("inventory_bottom_cta")
              }
            >
              Discuss Your Inventory Project →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          INTERNAL LINKS
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          className="inventory-container"
          style={{
            maxWidth: "900px",
          }}
        >
          <span className="inventory-label">
            Explore More HexxaTech Services
          </span>

          <h2
            style={{
              margin: "12px 0 20px",
              fontSize: "28px",
            }}
          >
            Other custom software solutions
          </h2>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <Link
              to="/services/custom-software-development"
              className="inventory-btn"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                color: "#334155",
              }}
            >
              Custom Software Development
            </Link>

            <Link
              to="/services/erp-business-management"
              className="inventory-btn"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                color: "#334155",
              }}
            >
              ERP & Business Management
            </Link>

            <Link
              to="/services/crm-lead-management"
              className="inventory-btn"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                color: "#334155",
              }}
            >
              CRM & Lead Management
            </Link>

            <Link
              to="/services/healthcare-software"
              className="inventory-btn"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                color: "#334155",
              }}
            >
              Healthcare Software
            </Link>

            <Link
              to="/services/mobile-app-development"
              className="inventory-btn"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                color: "#334155",
              }}
            >
              Mobile App Development
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}